/**
 * Analytics — Customer Success Phase 3 (Engagement client granulaire).
 *
 * Fire-and-forget : tous les appels sont best-effort, jamais bloquants.
 * Erreurs réseau / 4xx / 5xx sont silencieusement absorbées.
 *
 * Usage :
 *   import { trackPage, trackFeature, trackSearch, trackFilter, trackSession, useAutoPageTracking } from '../utils/analytics';
 *
 *   // Auto page tracking (à mettre une fois dans App.js)
 *   useAutoPageTracking();
 *
 *   // Sur clic d'un bouton métier
 *   <Button onClick={() => { trackFeature('operation.create'); doStuff(); }}>...
 */
import axios from 'axios';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRACK_URL = `${API}/analytics/track`;

// Événements postables côté client (whitelist côté backend aussi)
export const EVENT = {
  PAGE_VIEWED: 'page.viewed',
  PAGE_DURATION: 'page.duration',
  FEATURE_USED: 'feature.used',
  SEARCH_PERFORMED: 'search.performed',
  FILTER_APPLIED: 'filter.applied',
  SESSION_STARTED: 'session.started',
};

// Throttle léger pour éviter le flood (max 1 event identique / 500ms)
const _lastSent = new Map();
const THROTTLE_MS = 500;

function _post(eventType, metadata) {
  try {
    const key = `${eventType}|${JSON.stringify(metadata || {})}`;
    const now = Date.now();
    const prev = _lastSent.get(key);
    if (prev && now - prev < THROTTLE_MS) return;
    _lastSent.set(key, now);
    // Cleanup des entrées anciennes (>5min)
    if (_lastSent.size > 200) {
      for (const [k, t] of _lastSent.entries()) {
        if (now - t > 5 * 60 * 1000) _lastSent.delete(k);
      }
    }
    // Fire-and-forget (axios bénéficie de l'intercepteur CSRF central)
    axios.post(TRACK_URL, { eventType, metadata: metadata || {} }).catch(() => {});
  } catch (_) { /* never throw */ }
}

export function trackPage(path, extra = {}) {
  _post(EVENT.PAGE_VIEWED, { path, ...extra });
}

export function trackFeature(name, extra = {}) {
  if (!name) return;
  _post(EVENT.FEATURE_USED, { name, ...extra });
}

export function trackSearch(query, scope = 'global', extra = {}) {
  const q = String(query || '').trim();
  if (!q) return;
  _post(EVENT.SEARCH_PERFORMED, { query: q.slice(0, 200), scope, ...extra });
}

export function trackFilter(filterName, value, scope = 'unknown') {
  _post(EVENT.FILTER_APPLIED, { filterName, value: String(value).slice(0, 100), scope });
}

export function trackSession() {
  _post(EVENT.SESSION_STARTED, {
    userAgent: (navigator.userAgent || '').slice(0, 200),
    language: navigator.language,
    screenWidth: window.innerWidth,
  });
}

function _trackDuration(path, durationMs) {
  if (!path || durationMs <= 0 || durationMs > 4 * 60 * 60 * 1000) return; // ignore > 4h
  _post(EVENT.PAGE_DURATION, { path, durationMs: Math.round(durationMs) });
}

/**
 * Hook React qui log automatiquement chaque changement de route + durée
 * passée sur la page précédente. À mettre une seule fois dans App.js (ou
 * dans un Layout monté pour les routes client).
 */
export function useAutoPageTracking(opts = {}) {
  const location = useLocation();
  const lastPathRef = useRef(null);
  const enteredAtRef = useRef(Date.now());

  // Exclure routes admin / login / etc. (on track UNIQUEMENT le portail client)
  const isClientRoute = (path) => {
    if (!path) return false;
    if (path.startsWith('/admin')) return false;
    if (path.startsWith('/chauffeur')) return false;
    if (path === '/' || path === '/login' || path === '/register') return false;
    if (path.startsWith('/sign/')) return false;
    if (path.startsWith('/verify-')) return false;
    return true;
  };

  useEffect(() => {
    const path = location.pathname || '/';
    if (!isClientRoute(path)) {
      lastPathRef.current = null;
      return;
    }

    // Log durée sur page précédente
    if (lastPathRef.current && lastPathRef.current !== path) {
      _trackDuration(lastPathRef.current, Date.now() - enteredAtRef.current);
    }
    // Log page view actuelle
    trackPage(path);
    lastPathRef.current = path;
    enteredAtRef.current = Date.now();
  }, [location.pathname]);

  // Log la durée à l'unload (fermeture onglet, F5)
  useEffect(() => {
    const handler = () => {
      if (lastPathRef.current) {
        _trackDuration(lastPathRef.current, Date.now() - enteredAtRef.current);
      }
    };
    window.addEventListener('beforeunload', handler);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handler();
    });
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
}
